import { CircularProgress, Grid, Skeleton, Stack } from "@mui/material";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { IVendorInput } from "models/vendor";
import React, { useCallback, useEffect, useState } from "react";
import { useMutation } from "react-query";

import { addVendor } from "framework/vendor";
import { InputTypes } from "constants/enums";
import { IFormField } from "models/app";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import Form from "components/UI/Form";
import MuiButton from "components/UI/MuiButton";
import { getCountries } from "framework/countries";
import { QueryClient } from "react-query";

interface Props {
  feature: Feature;
}

const RegisterVendor: React.FC<Props> = ({ feature }) => {
  const { t } = useTranslation();

  const [inputObj, setInputObj] = useState<IVendorInput>({} as IVendorInput);
  const { setAlert } = useAlert();
  const [loading, setLoading] = useState<boolean>(false);
  const [vendorAdded, setVendorAdded] = useState<boolean>(false);
  const [countryOptions, setCountryOptions] = useState<any[]>([] as any[]);
  const queryClient = new QueryClient();
  const fetchCountries = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get countries", () =>
        getCountries()
      );
      let countryOptions = response.map((country: { id: any; name: any }) => {
        return { value: country.id, label: country.name };
      });
      setCountryOptions(countryOptions);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchCountries();
  }, []);

  let basicFields: IFormField[] = [
    {
      name: "Name",
      label: t("T_name"),
      type: InputTypes.TEXT,
      placeholder: t("T_enter_name"),
      internalLabel: true,
      required: true,
    },
    {
      name: "NameAr",
      label: `${t("T_name")} ${t("T_in_arabic")}`,
      type: InputTypes.TEXT,
      placeholder: `${t("T_enter_name")} ${t("T_in_arabic")}`,
      internalLabel: true,
      required: true,
    },
    {
      name: "RegistrationNumber",
      label: t("T_registration_number"),
      type: InputTypes.TEXT,
      placeholder: t("T_enter_registration_number"),
      internalLabel: true,
      required: true,
    },
    {
      name: "CountryId",
      label: t("T_country_name"),
      type: InputTypes.SELECT,
      placeholder: t("T_country"),
      options: countryOptions,
      multiple: false,
      internalLabel: true,
      required: true,
    },
    {
      name: "CountryCode",
      label: t("T_country_code"),
      type: InputTypes.COUNTRY,
      internalLabel: true,
      rows: 4,
      required: true,
    },
    {
      name: "Email",
      label: t("T_email"),
      type: InputTypes.EMAIL,
      placeholder: t("T_enter_email"),
      internalLabel: true,
      rows: 4,
      required: true,
    },
    {
      name: "PhoneNumber",
      label: t("T_phone_number"),
      type: InputTypes.TEXT,
      placeholder: t("T_enter_phone_number"),
      internalLabel: true,
      rows: 4,
      required: true,
    },
    {
      name: "Logo",
      label: t("T_Logo"),
      type: InputTypes.FILE,
      internalLabel: true,
    },
  ];

  const addVendorsMutation = useMutation({
    mutationFn: (createInput: FormData) => {
      return addVendor(createInput, "register");
    },
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAdd = async () => {
    if (!inputObj.Name || !inputObj.Logo || !inputObj.NameAr)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading(true);

      const createInput = new FormData();
      createInput.append("Name", inputObj.Name);
      createInput.append("NameAr", inputObj.NameAr);
      createInput.append("RegistrationNumber", inputObj.RegistrationNumber);
      createInput.append("CountryId", inputObj.CountryId);
      createInput.append("CountryCode", inputObj.CountryCode);
      createInput.append("PhoneNumber", inputObj.PhoneNumber);
      createInput.append("Email", inputObj.Email);
      if (inputObj.Logo) createInput.append("Logo", inputObj.Logo[0] || inputObj.Logo);
      await addVendorsMutation.mutateAsync(createInput);
      setVendorAdded(true);
      // setAlert({
      //   open: true,
      //   message: "Vendor added successfully",
      //   type: "success",
      // });
      // revalidate();
      setInputObj({} as IVendorInput);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }
  };

  return (
    <>
      {!vendorAdded && (
        <Stack spacing={2} px={20} py={2}>
          <Form fields={basicFields} onChange={handleChange} />
          <Grid
            container
            alignItems={"center"}
            justifyContent={"space-between"}
            px={1}
          >
            <MuiButton
              variant="contained"
              sx={{ bgcolor: "primary.main", color: "white" }}
              disabled={loading}
              size="large"
              onClick={handleAdd}
            >
              {t("T_add")}
              <CircularProgress
                size={10}
                sx={{ mx: 2, display: loading ? "" : "none" }}
              />
            </MuiButton>
          </Grid>
        </Stack>
      )}
      {vendorAdded && (
        <h3>
          Your account was successfully created, and you will be able to access it
          after the admin approves the registration.
        </h3>
      )}
    </>
  );

};

export default RegisterVendor;
